// src/components/common/Header.js
import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { LanguageContext } from "../../layouts/MainLayout";
import { ChevronDown } from "lucide-react";

const HeaderWrapper = styled.header`
  background-color: ${(props) => props.theme.colors.white};
  box-shadow: ${(props) => props.theme.shadows.light};
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 100;
`;

const NavbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 0.8rem 0;
  }
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.primary};
  text-decoration: none;
`;

const NavMenu = styled.ul`
  display: flex;
  list-style: none;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    display: ${(props) => (props.isOpen ? "flex" : "none")};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: ${(props) => props.theme.colors.white};
    padding: 1rem;
    box-shadow: ${(props) => props.theme.shadows.medium};
    z-index: 101;
  }
`;

const NavItem = styled.li``;

const NavLink = styled(Link)`
  color: ${(props) => props.theme.colors.dark};
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;

  &:hover,
  &.active {
    color: ${(props) => props.theme.colors.primary};
  }
`;

const AuthButton = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  margin-left: 1rem;
  border: 1px solid ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.colors.primary};
  background-color: transparent;
  border-radius: 0.5rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${(props) => props.theme.colors.primary};
    color: ${(props) => props.theme.colors.white};
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    display: ${(props) => (props.isOpen ? "flex" : "flex")};
    padding: 0.4rem 0.7rem;
    font-size: 0.9rem;
    margin-left: 0.5rem;
  }
`;

const MenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${(props) => props.theme.colors.dark};
  cursor: pointer;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    display: block;
  }
`;

// Dil seçimi için yeni bileşenler
const LanguageDropdown = styled.div`
  position: relative;
  display: inline-block;
  margin-left: 1rem;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    margin-left: 0.5rem;
  }
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background-color: transparent;
  border: 1px solid ${(props) => props.theme.colors.gray300};
  border-radius: 0.5rem;
  font-weight: 500;
  color: ${(props) => props.theme.colors.dark};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${(props) => props.theme.colors.primary};
  }

  svg {
    margin-left: 0.5rem;
  }

  img {
    width: 20px;
    height: 15px;
    margin-right: 6px;
    border-radius: 2px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 0.4rem 0.7rem;
    font-size: 0.9rem;

    img {
      width: 18px;
      height: 13px;
      margin-right: 4px;
    }

    svg {
      width: 14px;
      height: 14px;
      margin-left: 3px;
    }
  }
`;

const LanguageMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  min-width: 120px;
  background-color: ${(props) => props.theme.colors.white};
  border-radius: 0.5rem;
  box-shadow: ${(props) => props.theme.shadows.medium};
  overflow: hidden;
  z-index: 10;
  opacity: ${(props) => (props.isOpen ? "1" : "0")};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transform: ${(props) =>
    props.isOpen ? "translateY(0)" : "translateY(-10px)"};
  transition: all 0.3s ease;
`;

const LanguageOption = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.75rem 1rem;
  text-align: left;
  border: none;
  background-color: transparent;
  color: ${(props) =>
    props.active ? props.theme.colors.primary : props.theme.colors.dark};
  font-weight: ${(props) => (props.active ? "600" : "400")};
  cursor: pointer;
  transition: background-color 0.3s ease;

  img {
    width: 20px;
    height: 15px;
    margin-right: 8px;
    border-radius: 2px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.gray100};
  }
`;

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { language, setLanguage } = useContext(LanguageContext);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef(null);

  // Sayfa scroll edildiğinde header stilini değiştir
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Sayfa değiştiğinde menüyü kapat
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Dil menüsü dışında tıklama yapıldığında menüyü kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languageMenuRef.current &&
        !languageMenuRef.current.contains(event.target)
      ) {
        setIsLanguageMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleLanguageMenu = () => {
    setIsLanguageMenuOpen(!isLanguageMenuOpen);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLanguage(lng);
    setIsLanguageMenuOpen(false);
  };

  // Sayfa yollarına göre aktif nav link'i belirle
  const getNavLinkClass = (path) => {
    return location.pathname === path ? "active" : "";
  };

  // Dil koduna göre dil adını ve bayrak yolunu elde et
  const getLanguageInfo = (code) => {
    const languages = {
      tr: { name: "Türkçe", flag: "/assets/flags/tr.svg" },
      en: { name: "English", flag: "/assets/flags/gb.svg" },
    };
    return languages[code] || { name: code.toUpperCase(), flag: null };
  };

  return (
    <HeaderWrapper
      style={{
        boxShadow: scrolled
          ? "0 2px 10px rgba(0, 0, 0, 0.1)"
          : "0 2px 10px rgba(0, 0, 0, 0.05)",
      }}
    >
      <div className="container">
        <NavbarContainer>
          <Logo to="/">
            <img
              src="/assets/logos/iyitapu_yan.png"
              alt="İyiTapu"
              style={{
                width: "170px",
              }}
            ></img>
          </Logo>

          <NavMenu isOpen={isMenuOpen}>
            <NavItem>
              <NavLink to="/" className={getNavLinkClass("/")}>
                {t("navigation.home")}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/projects" className={getNavLinkClass("/projects")}>
                {t("navigation.projects")}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                to="/construction-companies"
                className={getNavLinkClass("/construction-companies")}
              >
                {t("navigation.constructionCompanies")}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/agents" className={getNavLinkClass("/agents")}>
                {t("navigation.realEstateAgents")}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/apply" className={getNavLinkClass("/apply")}>
                {t("navigation.apply")}
              </NavLink>
            </NavItem>
          </NavMenu>

          <div style={{ display: "flex", alignItems: "center" }}>
            <LanguageDropdown ref={languageMenuRef}>
              <LanguageButton onClick={toggleLanguageMenu}>
                <img
                  src={`/assets/flags/${
                    i18n.language === "tr" ? "tr" : "gb"
                  }.svg`}
                  alt={i18n.language === "tr" ? "Türk bayrağı" : "English flag"}
                  style={{ width: "20px", height: "15px", marginRight: "6px" }}
                />
                {i18n.language === "tr" ? "Türkçe" : "English"}
                <ChevronDown size={16} />
              </LanguageButton>
              <LanguageMenu isOpen={isLanguageMenuOpen}>
                <LanguageOption
                  onClick={() => changeLanguage("en")}
                  active={language === "en"}
                >
                  <img src="/assets/flags/gb.svg" alt="British flag" />
                  English
                </LanguageOption>
                <LanguageOption
                  onClick={() => changeLanguage("tr")}
                  active={language === "tr"}
                >
                  <img src="/assets/flags/tr.svg" alt="Türk bayrağı" />
                  Türkçe
                </LanguageOption>
              </LanguageMenu>
            </LanguageDropdown>

            <AuthButton to="https://app.iyitapu.com/login" isOpen={isMenuOpen}>
              {t("navigation.login")}
            </AuthButton>

            <MenuToggle onClick={toggleMenu}>
              {isMenuOpen ? "✕" : "☰"}
            </MenuToggle>
          </div>
        </NavbarContainer>
      </div>
    </HeaderWrapper>
  );
};

export default Header;
